import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PublicHeader from "@/components/PublicHeader";
import useSeo from "@/hooks/useSeo";
import { Library, ChevronRight, Sparkles } from "lucide-react";

const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

export default function PublicTestsListPage() {
  const [state, setState] = useState({ loading: true, error: null, tests: [] });
  useSeo({
    title: "Testes de Saúde Gratuitos | Centro de Performance Farmacêutica",
    description: "Faça testes rápidos de saúde metabólica, intestinal e hormonal e receba seu score com orientações de um farmacêutico clínico em Tucuruí-PA.",
    path: "/testes",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get("/public/testes");
        setState({ loading: false, error: null, tests: data || [] });
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "Não foi possível carregar os testes.", tests: [] });
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="public-tests-page">
      <PublicHeader />
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <div className="mb-10">
          <div className="overline text-gold mb-3 inline-flex items-center gap-2 tracking-[0.24em]">
            <Library className="w-3.5 h-3.5" /> Biblioteca de Testes
          </div>
          <h1 className="brand-wordmark text-3xl md:text-5xl font-bold uppercase text-primary leading-tight">
            Descubra o próximo passo para a sua saúde.
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">
            Testes rápidos, com análise personalizada. Escolha um teste abaixo e receba
            seu score e orientações direto no WhatsApp.
          </p>
        </div>

        {state.loading ? (
          <div className="py-16 text-center text-sm text-muted-foreground overline">Carregando…</div>
        ) : state.error ? (
          <div className="py-16 text-center text-sm text-destructive">{state.error}</div>
        ) : state.tests.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground bg-card border border-dashed border-border rounded-sm">
            Nenhum teste disponível no momento. Volte em breve.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {state.tests.map((t) => (
              <Link
                key={t.id}
                to={`/testes/${t.slug}`}
                className="group bg-card border border-border rounded-md p-6 hover:border-primary transition-colors block"
                data-testid={`test-card-${t.slug}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="overline text-gold mb-2 flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3" /> Teste rápido
                    </div>
                    <h2 className="brand-wordmark uppercase text-lg md:text-xl font-bold text-primary leading-tight group-hover:text-gold transition-colors">
                      {t.name}
                    </h2>
                    {t.description && (
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-3">
                        {t.description}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0 mt-1" />
                </div>
                <div className="mt-4 text-xs text-primary font-semibold inline-flex items-center gap-1">
                  Começar agora <ChevronRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
